<script setup lang="ts">
import { PromosOutputParams, VacationType } from "@/../types/promos-types";
import { VacvDropDown } from "vacv-components";
import { Option, VacvDropDownOption } from "@/../types/option";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import { computed } from "vue";

interface Props {
  promosParams: PromosOutputParams;
  tripDepartureOptions: Option[];
  leaveFromOptions: Option[];
  travelToOptions: Option[];
  durationOptions: Option[];
  starRatingOptions: Option[];
  vacationType: VacationType;
  isDurationsLoading: boolean;
}
const props = defineProps<Props>();

const emits = defineEmits<{
  (e: "search"): void;
  (e: "departureMonthChange", newValue: VacvDropDownOption): void;
  (e: "leaveFromChange", newValue: VacvDropDownOption): void;
  (e: "travelToChange", newValue: VacvDropDownOption): void;
  (e: "durationChange", newValue: VacvDropDownOption): void;
  (e: "starRatingChange", newValue: VacvDropDownOption): void;
}>();

const onSearch = (): void => {
  emits("search");
};

const onDepartureMonthChanged = (
  oldValue: VacvDropDownOption,
  newValue: VacvDropDownOption
): void => {
  emits("departureMonthChange", newValue);
};

const onLeaveFromChanged = (
  oldValue: VacvDropDownOption,
  newValue: VacvDropDownOption
): void => {
  emits("leaveFromChange", newValue);
};

const onTravelToChanged = (
  oldValue: VacvDropDownOption,
  newValue: VacvDropDownOption
): void => {
  emits("travelToChange", newValue);
};

const onDurationChanged = (
  oldValue: VacvDropDownOption,
  newValue: VacvDropDownOption
): void => {
  emits("durationChange", newValue);
};

const onStarRatingChanged = (
  oldValue: VacvDropDownOption,
  newValue: VacvDropDownOption
): void => {
  emits("starRatingChange", newValue);
};

const isTour = computed((): boolean => {
  return props.vacationType === VacationType.TOUR;
});

// expose methods for unit tests
defineExpose({
  onSearch,
  onDepartureMonthChanged,
  onDurationChanged,
  onLeaveFromChanged,
  onTravelToChanged,
  onStarRatingChanged,
});
</script>

<template>
  <form class="promos-search-form">
    <div
      class="promos-search-form__grid"
      :class="{ 'promos-search-form__grid--tour': isTour }"
    >
      <div>
        <vacv-drop-down
          :label="$t('search-form.trip-departure-month')"
          :initial-value="promosParams.year_month"
          :selected-value="promosParams.year_month"
          :options="tripDepartureOptions"
          :outlined="true"
          :open-left="true"
          drop-down-id="trip-departure-month"
          @value-change="onDepartureMonthChanged"
        />
      </div>
      <div>
        <vacv-drop-down
          :label="$t('search-form.leave-from')"
          :initial-value="promosParams.gateway"
          :selected-value="promosParams.gateway"
          :options="leaveFromOptions"
          :outlined="true"
          :open-left="true"
          drop-down-id="leaveFrom"
          @value-change="onLeaveFromChanged"
        />
      </div>
      <div>
        <vacv-drop-down
          :label="$t('search-form.travel-to')"
          :initial-value="promosParams.destination"
          :selected-value="promosParams.destination"
          :options="travelToOptions"
          :outlined="true"
          :open-left="true"
          drop-down-id="travelTo"
          @value-change="onTravelToChanged"
        />
      </div>
      <div v-if="!isTour">
        <vacv-drop-down
          :label="$t('search-form.duration')"
          :initial-value="promosParams.duration"
          :selected-value="promosParams.duration"
          :options="durationOptions"
          :outlined="true"
          :open-left="true"
          drop-down-id="duration"
          @value-change="onDurationChanged"
        />
        <loading-spinner v-if="isDurationsLoading" />
      </div>
      <div>
        <vacv-drop-down
          :label="$t('search-form.star-rating')"
          :initial-value="promosParams.starRating"
          :selected-value="promosParams.starRating"
          :options="starRatingOptions"
          :outlined="true"
          :open-left="true"
          :is-star-rating="true"
          drop-down-id="starRating"
          @value-change="onStarRatingChanged"
        />
      </div>
      <div>
        <button
          type="button"
          class="vacv-btn-secondary btn-search"
          @click="onSearch"
          :disabled="isDurationsLoading"
        >
          {{ $t("plp.labels.search") }}
        </button>
      </div>
    </div>
  </form>
</template>

<style lang="scss">
@use "@/assets/scss/product-listing/promos-search-form.scss" as *;
</style>
